/**
 * Building blocks of the trader money screens in the editorial language (ADR-0029, docs/design/editorial-app.md):
 * the page head, the buy → pay → account stepper, the one large figure, pill fields and status pills.
 */
import { Button, cn } from "@prop/ui";
import { Check, Copy } from "lucide-react";
import { useEffect, useId, useState } from "react";
import { useMoneyT } from "../../i18n";

/** A flat panel one tone off the canvas. */
export const PANEL = "glass-panel rounded-[14px] p-5 sm:p-6";
/** The one action that buys or pays out: the brand's lime with ink, in every theme. */
export const LIME = "[--action-bg:#d4ff24] [--action-hover-bg:#c4ee12] [--action-fg:#030407]";
/** Pill-shaped text field and select, matching the pill buttons. */
export const FIELD = "h-10 w-full min-w-0 rounded-full border border-foreground/15 bg-transparent px-4 text-sm tabular-nums outline-none focus-visible:ring-2 focus-visible:ring-ring";

export function PageHead({ title, lead, children }: { title: string; lead?: string; children?: React.ReactNode }) {
  return (
    <header className="flex flex-wrap items-end justify-between gap-x-8 gap-y-4">
      <div className="flex min-w-0 max-w-[60ch] flex-col gap-3">
        <h1 className="break-words">{title}</h1>
        {lead && <p className="text-pretty text-muted-foreground">{lead}</p>}
      </div>
      {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
    </header>
  );
}

/** The key figure of a screen: wide, large, its label under it. */
export function Figure({ value, label, className }: { value: string; label: React.ReactNode; className?: string }) {
  return (
    <div className={cn("flex min-w-0 flex-col gap-2", className)}>
      <p className="break-words font-display text-[clamp(2.25rem,11vw,3rem)] font-semibold leading-none tracking-tight tabular-nums [font-stretch:120%] sm:text-6xl">{value}</p>
      <p className="text-sm opacity-75">{label}</p>
    </div>
  );
}

/** Buy → pay → account: the purchase as one connected flow (UX law 5). `at` is the current step; past ones are done. */
export function Stepper({ at }: { at: 0 | 1 | 2 | 3 }) {
  const t = useMoneyT();
  const steps = [t("steps.program"), t("steps.pay"), t("steps.account")];
  return (
    <ol className="flex items-center gap-2 text-sm" aria-label={steps.join(" → ")}>
      {steps.map((s, i) => {
        const done = i < at;
        const now = i === at;
        return (
          <li key={s} className={cn("flex min-w-0 items-center gap-2", i < steps.length - 1 && "flex-1")} aria-current={now ? "step" : undefined}>
            <span
              className={cn(
                "inline-flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold tabular-nums transition-colors duration-200",
                done && "bg-foreground text-background",
                now && "bg-[#d4ff24] text-[#030407]",
                !done && !now && "border border-foreground/20 text-muted-foreground",
              )}
            >
              {done ? <Check className="size-3.5" aria-hidden="true" /> : i + 1}
            </span>
            <span className={cn("truncate", now ? "font-semibold" : "hidden text-muted-foreground sm:inline")}>{s}</span>
            {i < steps.length - 1 && <span aria-hidden="true" className={cn("h-px min-w-4 flex-1", done ? "bg-foreground" : "bg-foreground/15")} />}
          </li>
        );
      })}
    </ol>
  );
}

/** Codes, addresses and test numbers: no autofill, no spelling marks, no auto-capitalised first letter on phones. */
export function TextField({
  label,
  value,
  onChange,
  inputMode,
  placeholder,
  maxLength,
  hint,
  invalid,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  inputMode?: "numeric" | "text";
  placeholder?: string;
  maxLength?: number;
  hint?: React.ReactNode;
  invalid?: boolean;
}) {
  const id = useId();
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>
      <input
        id={id}
        className={cn(FIELD, invalid && "border-destructive/60")}
        value={value}
        inputMode={inputMode}
        placeholder={placeholder}
        maxLength={maxLength}
        aria-invalid={invalid || undefined}
        aria-describedby={hint ? `${id}-hint` : undefined}
        autoComplete="off"
        autoCapitalize="off"
        spellCheck={false}
        onChange={(e) => onChange(e.target.value)}
      />
      {hint && (
        <p id={`${id}-hint`} className={cn("text-xs", invalid ? "text-destructive" : "text-muted-foreground")}>
          {hint}
        </p>
      )}
    </div>
  );
}

/** Copy a code or an address; the label turns to "copied" for two seconds. */
export function CopyButton({ text, label, done }: { text: string; label: string; done: string }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);
  return (
    <Button size="sm" variant="outline" className="shrink-0" onClick={() => void navigator.clipboard?.writeText(text).then(() => setCopied(true))}>
      {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />} {copied ? done : label}
    </Button>
  );
}

/** A status word on a quiet pill with a dot; `tone` tints it for success, waiting or trouble. */
export function StatusPill({ children, tone }: { children: React.ReactNode; tone?: "ok" | "bad" | "wait" }) {
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium leading-none",
        tone === "ok" && "bg-emerald-500/12 text-emerald-700 dark:text-emerald-300",
        tone === "bad" && "bg-destructive/12 text-destructive",
        tone === "wait" && "bg-amber-500/12 text-amber-700 dark:text-amber-300",
        !tone && "bg-muted text-muted-foreground",
      )}
    >
      <span className="size-1.5 shrink-0 rounded-full bg-current" aria-hidden="true" />
      <span className="inline-block first-letter:uppercase">{children}</span>
    </span>
  );
}

/** Sum of decimal money strings ("12.50") in cents, back to a decimal string, without a float in between. */
export function sumMoney(values: string[]): string {
  const cents = values.reduce((acc, s) => {
    const [whole = "0", frac = ""] = s.split(".");
    return acc + BigInt(whole) * 100n + BigInt(`${frac}00`.slice(0, 2));
  }, 0n);
  return `${cents / 100n}.${String(cents % 100n).padStart(2, "0")}`;
}
